import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, Search, Grid, List, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import { NoteCard } from '@/components/notes/NoteCard';
import { CreateNoteDialog } from '@/components/notes/CreateNoteDialog';
import { NoteEditor } from '@/components/notes/NoteEditor';
import { useToast } from '@/hooks/use-toast';

type Note = {
  id: string;
  title: string;
  content: any;
  category: 'general' | 'pinned' | 'ideas' | 'reminders';
  color: string;
  visibility: 'private' | 'board_shared';
  is_pinned: boolean;
  reminder_date?: string;
  reminder_sent: boolean;
  position: number;
  tags: string[];
  created_at: string;
  updated_at: string;
  board_id?: string;
  user_id: string;
};

type Board = {
  id: string;
  title: string;
};

const Notes = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notes, setNotes] = useState<Note[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBoardContext, setSelectedBoardContext] = useState<string>('private');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  // Fetch notes and boards on component mount
  useEffect(() => {
    if (user) {
      fetchNotes();
      fetchBoards();
    }
  }, [user]);

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('reminder_notes')
        .select('*')
        .order('position', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast({
        title: "Error",
        description: "Failed to load notes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = async () => {
    try {
      const { data, error } = await supabase
        .from('boards')
        .select('id, title')
        .order('title');

      if (error) throw error;
      setBoards(data || []);
    } catch (error) {
      console.error('Error fetching boards:', error);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    // Create new array with reordered items
    const filteredNotes = getFilteredNotes();
    const newNotes = Array.from(filteredNotes);
    const [reorderedItem] = newNotes.splice(sourceIndex, 1);
    newNotes.splice(destIndex, 0, reorderedItem);

    // Update positions
    const updatedNotes = newNotes.map((note, index) => ({
      ...note,
      position: index
    }));

    // Update the main notes array
    const updatedAllNotes = notes.map(note => {
      const updatedNote = updatedNotes.find(n => n.id === note.id);
      return updatedNote || note;
    });

    setNotes(updatedAllNotes);

    // Update positions in database
    try {
      const updates = updatedNotes.map((note, index) => ({
        id: note.id,
        position: index
      }));

      for (const update of updates) {
        await supabase
          .from('reminder_notes')
          .update({ position: update.position })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating note positions:', error);
      // Revert on error
      fetchNotes();
    }
  };

  const getFilteredNotes = () => {
    return notes.filter(note => {
      const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           note.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || 
                            (selectedCategory === 'pinned' && note.is_pinned) ||
                            note.category === selectedCategory;

      // Filter by board context
      const matchesBoardContext = (() => {
        if (selectedBoardContext === 'private') {
          return note.board_id === null;
        } else {
          return note.board_id === selectedBoardContext;
        }
      })();

      return matchesSearch && matchesCategory && matchesBoardContext;
    });
  };

  const filteredNotes = getFilteredNotes();
  const pinnedNotes = filteredNotes.filter(note => note.is_pinned);
  const unpinnedNotes = filteredNotes.filter(note => !note.is_pinned);

  // Get note counts for tabs
  const getNotesCountForBoard = (boardId: string | null) => {
    return notes.filter(note => note.board_id === boardId).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-muted-foreground">Loading notes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-6 py-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Notes</h1>
            <p className="text-muted-foreground mt-1">
              Capture ideas, reminders, and thoughts
            </p>
          </div>
          
          <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Note
          </Button>
        </div>

        {/* Board Context Selector */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-foreground mb-2">Note Context</h2>
            <p className="text-sm text-muted-foreground">Switch between your private notes and board-specific notes</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant={selectedBoardContext === 'private' ? 'default' : 'outline'}
              onClick={() => setSelectedBoardContext('private')}
              className="flex items-center gap-2"
            >
              Private Notes
              <Badge variant="secondary" className="ml-1">{getNotesCountForBoard(null)}</Badge>
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={selectedBoardContext !== 'private' ? 'default' : 'outline'}
                  className="flex items-center gap-2"
                >
                  {selectedBoardContext !== 'private' 
                    ? boards.find(b => b.id === selectedBoardContext)?.title || 'Board Notes'
                    : 'Board Notes'
                  }
                  <ChevronDown className="h-4 w-4" />
                  {selectedBoardContext !== 'private' && (
                    <Badge variant="secondary" className="ml-1">
                      {getNotesCountForBoard(selectedBoardContext)}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {boards.map((board) => {
                  const noteCount = getNotesCountForBoard(board.id);
                  return (
                    <DropdownMenuItem
                      key={board.id}
                      onClick={() => setSelectedBoardContext(board.id)}
                      className="flex items-center justify-between"
                    >
                      <span className="truncate">{board.title}</span>
                      <Badge variant="outline" className="ml-2">{noteCount}</Badge>
                    </DropdownMenuItem>
                  );
                })}
                {boards.length === 0 && (
                  <DropdownMenuItem disabled>
                    No boards available
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Current Context Display */}
        <div className="mb-6 p-4 bg-card border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-foreground">
                {selectedBoardContext === 'private' 
                  ? 'Private Notes' 
                  : boards.find(b => b.id === selectedBoardContext)?.title
                }
              </h3>
              <p className="text-sm text-muted-foreground">
                {selectedBoardContext === 'private' 
                  ? 'Personal notes visible only to you'
                  : 'Board notes visible to team members'
                }
              </p>
            </div>
            <Badge variant="secondary" className="text-lg px-3 py-1">
              {getNotesCountForBoard(selectedBoardContext === 'private' ? null : selectedBoardContext)} notes
            </Badge>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search notes and tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pinned">Pinned</TabsTrigger>
                <TabsTrigger value="ideas">Ideas</TabsTrigger>
                <TabsTrigger value="reminders">Reminders</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex items-center gap-1 ml-4">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Notes Grid/List */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="space-y-8">
            {/* Pinned Notes */}
            {pinnedNotes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Pinned</h2>
                  <Badge variant="secondary">{pinnedNotes.length}</Badge>
                </div>
                
                <Droppable droppableId="pinned-notes">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`
                        ${viewMode === 'grid'
                          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                          : 'space-y-2'
                      }`}
                    >
                      {pinnedNotes.map((note, index) => (
                        <Draggable key={note.id} draggableId={note.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <NoteCard
                                note={note}
                                viewMode={viewMode}
                                isDragging={snapshot.isDragging}
                                onEdit={() => setEditingNote(note)}
                                onRefresh={fetchNotes}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}

            {/* Other Notes */}
            {unpinnedNotes.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    {selectedCategory === 'all' ? 'Notes' : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                  </h2>
                  <Badge variant="secondary">{unpinnedNotes.length}</Badge>
                </div>
                
                <Droppable droppableId="notes">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`
                        ${viewMode === 'grid'
                          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                          : 'space-y-2'
                      }`}
                    >
                      {unpinnedNotes.map((note, index) => (
                        <Draggable key={note.id} draggableId={note.id} index={pinnedNotes.length + index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <NoteCard
                                note={note}
                                viewMode={viewMode}
                                isDragging={snapshot.isDragging}
                                onEdit={() => setEditingNote(note)}
                                onRefresh={fetchNotes}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}

            {/* Empty State */}
            {filteredNotes.length === 0 && (
              <div className="text-center py-12">
                <div className="text-muted-foreground text-lg mb-4">
                  {searchQuery ? 'No notes match your search' :
                   selectedBoardContext === 'private' ? 'No private notes yet' :
                   `No notes for ${boards.find(b => b.id === selectedBoardContext)?.title} yet`}
                </div>
                {!searchQuery && (
                  <Button onClick={() => setShowCreateDialog(true)}>
                    Create your first note
                  </Button>
                )}
              </div>
            )}
          </div>
        </DragDropContext>
      </div>

      {/* Dialogs */}
      <CreateNoteDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onNoteCreated={fetchNotes}
      />
      
      {editingNote && (
        <NoteEditor
          note={editingNote}
          open={!!editingNote}
          onOpenChange={(open) => !open && setEditingNote(null)}
          onNoteSaved={fetchNotes}
        />
      )}
    </div>
  );
};

export default Notes;
